import { useState } from 'react'
import { Routes, Route, NavLink, Outlet, useNavigate } from 'react-router-dom'
import reportingApi from '../../services/reportingApi'
import { T, page } from './ui'
import BoardHome from './BoardHome'
import CityDesk from './CityDesk'
import VenueBoard from './VenueBoard'
import CourtScreen from './CourtScreen'
import ResultsBrowser from './ResultsBrowser'
import Leaderboards from './Leaderboards'
import Overview from './Overview'

/**
 * The results desk.
 *
 * Every view is addressable, because a screen is set up once and then left
 * alone: paste the URL of a court into a television's browser and it comes back
 * to the same court after a power cut.
 */
const ReportingApp = () => {
  const [user, setUser] = useState(reportingApi.current())

  if (!user) return <SignIn onDone={setUser} />

  return (
    <Routes>
      {/* The court screen fills the display, so it carries no navigation. */}
      <Route path="screen/:city/:sport/:court" element={<CourtScreen />} />

      <Route element={<Shell user={user} onSignOut={() => setUser(null)} />}>
        <Route index element={<BoardHome />} />
        <Route path="city/:city" element={<CityDesk />} />
        <Route path="venue/:city/:sport" element={<VenueBoard />} />
        <Route path="results" element={<ResultsBrowser />} />
        <Route path="leaderboards" element={<Leaderboards />} />
        <Route path="leaderboards/:sport" element={<Leaderboards />} />
        <Route path="overview" element={<Overview />} />
      </Route>
    </Routes>
  )
}

const Shell = ({ user, onSignOut }) => {
  const navigate = useNavigate()

  const signOut = () => {
    reportingApi.logout()
    onSignOut()
    navigate('/board')
  }

  return (
    <div style={{ background: T.ground, minHeight: '100vh' }}>
      <header style={{
        background: T.card, borderBottom: `1px solid ${T.line}`, padding: '0 24px',
      }}>
        <div style={{
          maxWidth: 1180, margin: '0 auto', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', padding: '14px 0',
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 19, color: T.ink }}>Results Desk</h1>
            <p style={{ margin: '2px 0 0', color: T.muted, fontSize: 12.5 }}>{user.full_name || user.name}</p>
          </div>

          <nav style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              ['/board', 'Screens', true],
              ['/board/results', 'Results'],
              ['/board/leaderboards', 'Leaderboards'],
              ['/board/overview', 'All cities'],
            ].map(([to, label, end]) => (
              <NavLink key={to} to={to} end={end} style={({ isActive }) => ({
                padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13.5,
                textDecoration: 'none',
                background: isActive ? T.accent : 'transparent',
                color: isActive ? '#fff' : T.muted,
              })}>{label}</NavLink>
            ))}
          </nav>

          <button onClick={signOut} style={{
            padding: '8px 16px', background: '#fff', border: `1px solid ${T.line}`,
            borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, color: T.muted,
          }}>Sign out</button>
        </div>
      </header>

      <Outlet />
    </div>
  )
}

const SignIn = ({ onDone }) => {
  const [form, setForm] = useState({ username: '', password: '' })
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onDone(await reportingApi.login(form.username, form.password))
    } catch (err) {
      setError(err.response?.data?.message || 'Could not sign in.')
      setBusy(false)
    }
  }

  return (
    <div style={{ ...page, display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <form onSubmit={submit} style={{
        width: 'min(400px, 92vw)', background: '#fff', border: `1px solid ${T.line}`,
        borderRadius: 14, padding: 28,
      }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Results Desk</h1>
        <p style={{ margin: '6px 0 20px', color: T.muted, fontSize: 13.5 }}>
          Sign in to run the screens, look up a result, or read the leaderboards.
        </p>

        {error && <div style={{
          padding: 11, marginBottom: 14, background: '#fdeceb', border: '1px solid #f3c4c0',
          borderRadius: 8, color: T.accent, fontSize: 13,
        }}>{error}</div>}

        <label style={lbl}>Username
          <input value={form.username} autoFocus
                 onChange={(e) => setForm({ ...form, username: e.target.value })} style={inp} />
        </label>
        <label style={{ ...lbl, marginTop: 12 }}>Password
          <input type="password" value={form.password}
                 onChange={(e) => setForm({ ...form, password: e.target.value })} style={inp} />
        </label>

        <button type="submit" disabled={busy} style={{
          padding: '11px 18px', background: T.accent, color: '#fff', border: 0,
          borderRadius: 8, cursor: 'pointer', fontWeight: 700, marginTop: 20, width: '100%',
        }}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
    </div>
  )
}

const lbl = { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, color: T.muted, fontWeight: 600 }
const inp = { padding: '10px 12px', border: '1px solid #d9dee5', borderRadius: 8, fontSize: 14 }

export default ReportingApp
